import { FC, useEffect, useMemo, useState } from "react"
import { Search, X } from "lucide-react"
import BBModal from "../../../modal"
import Select from "../../../form/select"
import { PrimaryBtn, DefaultBtn } from "../../../buttons"
import Skeleton from "../../../skeletons/base"
import {
  DefaultProfileOption,
  ScenarioPreferenceMapResponse
} from "../../../../../types"

interface Props {
  show: boolean
  onClose: () => void
  data: ScenarioPreferenceMapResponse | undefined
  isLoading: boolean
  isSaving: boolean
  onSave: (preferences: Record<string, number | null>) => void
}

const ScenarioPreferencesModal: FC<Props> = ({
  show,
  onClose,
  data,
  isLoading,
  isSaving,
  onSave
}) => {
  const [staged, setStaged] = useState<Record<string, number | null>>({})
  const [search, setSearch] = useState("")

  useEffect(() => {
    if (data?.profile_preferences) {
      setStaged({ ...data.profile_preferences })
    }
  }, [data])

  useEffect(() => {
    if (!show) setSearch("")
  }, [show])

  const profiles = data?.default_profiles ?? []

  const assignedIds = useMemo(
    () => new Set(Object.values(staged).filter((v): v is number => v != null)),
    [staged]
  )

  const getAvailableProfiles = (currentValue: number | null) =>
    profiles.filter(p => p.id === currentValue || !assignedIds.has(p.id))

  const findProfileTitle = (id: number | null) =>
    profiles.find(p => p.id === id)?.title ?? null

  const handleAssign = (key: string, profileId: string) => {
    setStaged(prev => ({ ...prev, [key]: Number(profileId) }))
  }

  const handleRemove = (key: string) => {
    setStaged(prev => ({ ...prev, [key]: null }))
  }

  const handleSave = () => onSave(staged)

  const handleCancel = () => {
    if (data?.profile_preferences) {
      setStaged({ ...data.profile_preferences })
    }
    onClose()
  }

  const scenarioKeys = Object.keys(staged)
  const needle = search.toLowerCase().trim()
  const filteredKeys = needle
    ? scenarioKeys.filter(k => k.toLowerCase().includes(needle))
    : scenarioKeys

  return (
    <BBModal
      show={show}
      onClose={handleCancel}
      title="Scenario Preferences"
      size="xl"
    >
      {isLoading ? (
        <Skeleton>
          <div className="mb-3 h-9 w-full rounded-md bg-gray-200" />
          <div className="divide-y divide-gray-100">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="flex items-center gap-4 py-3">
                <div className="h-4 w-20 rounded bg-gray-200" />
                {i % 3 === 0 ? (
                  <div className="flex flex-1 items-center justify-between">
                    <div className="h-4 w-24 rounded bg-gray-200" />
                    <div className="h-3.5 w-3.5 rounded bg-gray-200" />
                  </div>
                ) : (
                  <div className="h-9 flex-1 rounded-md bg-gray-200" />
                )}
              </div>
            ))}
          </div>
        </Skeleton>
      ) : scenarioKeys.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">
          No scenario preferences available for this brand.
        </p>
      ) : (
        <>
          <div className="relative mb-3">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter by scenario key…"
              className="w-full rounded-md border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="max-h-[28rem] divide-y divide-gray-100 overflow-y-auto">
            {filteredKeys.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400">
                No keys matching &ldquo;{search}&rdquo;
              </p>
            ) : (
              filteredKeys.map(key => {
                const value = staged[key]
                const title = findProfileTitle(value)
                const isAssigned = value != null && title != null

                return (
                  <div key={key} className="flex items-center gap-4 py-3">
                    <span className="w-44 min-w-0 shrink-0 text-sm text-gray-700 break-words">
                      {key.replace(/_/g, " ")}
                    </span>

                    {isAssigned ? (
                      <div className="flex flex-1 min-w-0 items-center justify-between gap-2">
                        <span className="text-sm text-gray-400 truncate">
                          {title}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemove(key)}
                          className="shrink-0 text-gray-300 transition-colors hover:text-red-500"
                          aria-label={`Remove ${key} mapping`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex-1 min-w-0">
                        <Select
                          data={getAvailableProfiles(value).map(p => ({
                            id: String(p.id),
                            name: p.title
                          }))}
                          selected={value != null ? String(value) : ""}
                          label={`Profile for ${key}`}
                          showLabel={false}
                          setSelected={(id: string) => handleAssign(key, id)}
                        />
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </>
      )}

      <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-4">
        <DefaultBtn onClick={handleCancel} disabled={isSaving}>
          Cancel
        </DefaultBtn>
        <PrimaryBtn
          onClick={handleSave}
          isLoading={isSaving}
          disabled={isLoading || isSaving}
        >
          Save
        </PrimaryBtn>
      </div>
    </BBModal>
  )
}

export default ScenarioPreferencesModal
